import React, { useMemo } from 'react';

import {
  ContentTraverserContextProvider,
  RicosAlignmentContextProvider,
  RicosContextProvider,
  RicosNodeContextProvider,
  RicosViewerContextProvider,
  SpoilerContextProvider,
  VisibleIndentationContextProvider,
  ZIndexContextProvider,
} from './../../ricos-context/index.js';
import { NodeTreeMapContextProvider } from './../../ricos-context/viewer/NodeTreeMapContext.js';
import { ZIndexService } from './../../ricos-styles/index.js';
import { BiHelpersProvider } from './../../_shared/bi-helpers-context/bi-helpers-context.js';
import { isDefined } from './../../wix-rich-content-common/index.js';

import styles from '../statics/rich-content-viewer-scss.js';
import NodeViewDecorators, { getDecorators } from './rericos/decorators/index.js';
import renderers from './rericos/renderers/index.js';
import { getPluginRenderers } from './rericos/renderers/plugins/index.js';
import { SpoilerManager } from './rericos/utils/SpoilerManager.js';
import RicosNodesRenderer from './RicosNodesRenderer.js';
import type { ContentTraverserProps } from './types.js';

const ContentTraverser = ({
  content,
  context,
  typeMappers,
  nodeViewRenderers,
  nodeViewDecorators,
  SpoilerViewerWrapper,
  addAnchorsPrefix,
}: ContentTraverserProps) => {
  // When AI streams sometimes we get empty nodes, so we filter them out here
  const nodes = (content.nodes ?? []).filter(isDefined);

  const spoilerManager = new SpoilerManager();

  const allRenderers = useMemo(
    () => ({
      ...renderers,
      ...getPluginRenderers(context, typeMappers, nodeViewRenderers, SpoilerViewerWrapper),
    }),
    []
  );
  const allDecorators = useMemo(() => {
    const decorators = getDecorators(context.plugins);
    return NodeViewDecorators.of([...decorators, ...nodeViewDecorators], context);
  }, [context]);

  const ricosContext = useMemo(() => {
    const { t, locale, isMobile, theme, experiments, languageDir, jsdomMode } = context;
    return {
      t,
      locale,
      localeContent: locale,
      isMobile,
      theme,
      experiments,
      languageDir,
      jsdomMode,
      tabIndex: 0,
    };
  }, [
    context.t,
    context.locale,
    context.isMobile,
    context.theme,
    context.experiments,
    context.languageDir,
    context.jsdomMode,
  ]);

  const zIndexService = useMemo(() => {
    return new ZIndexService(context.isMobile, context.theme.settingsStyles);
  }, []);

  return (
    <>
      <RicosContextProvider {...ricosContext}>
        <ZIndexContextProvider zIndexService={zIndexService}>
          <ContentTraverserContextProvider
            nodeViewDecorators={allDecorators}
            renderers={allRenderers}
            SpoilerViewerWrapper={SpoilerViewerWrapper}
          >
            <RicosViewerContextProvider contextualData={context}>
              <BiHelpersProvider helpers={context.helpers || {}}>
                <RicosAlignmentContextProvider textAlignment={context.textAlignment}>
                  <SpoilerContextProvider spoilerManager={spoilerManager}>
                    <VisibleIndentationContextProvider visibleIndentation={null}>
                      <RicosNodeContextProvider parentNode={null} parentIndex={0}>
                        <NodeTreeMapContextProvider nodes={nodes}>
                          <div className={styles.rootedTraversedContent}>
                            <RicosNodesRenderer
                              nodes={nodes}
                              isRoot
                              addAnchorsPrefix={addAnchorsPrefix}
                              shouldParagraphApplyTextStyle
                            />
                          </div>
                        </NodeTreeMapContextProvider>
                      </RicosNodeContextProvider>
                    </VisibleIndentationContextProvider>
                  </SpoilerContextProvider>
                </RicosAlignmentContextProvider>
              </BiHelpersProvider>
            </RicosViewerContextProvider>
          </ContentTraverserContextProvider>
        </ZIndexContextProvider>
      </RicosContextProvider>
    </>
  );
};

export default ContentTraverser;
